import { differenceInCalendarDays } from 'date-fns';
import { DrillScoring } from '../enums/drill-scoring.enum';
import { TranslationService } from '../services/translation.service';
import { QuickStartDto } from './dto/quick-start.dto';
import {
  QuickStartLastDrillViewModel,
  QuickStartTodayPlanViewModel,
  QuickStartVariant,
  QuickStartViewModel
} from './models/quick-start.view-model';

const scoringShort = (scoring: DrillScoring): string => {
  switch (scoring) {
    case DrillScoring.TimePerRep:
      return 'TPR';
    case DrillScoring.RepsOnly:
      return 'REPS';
    case DrillScoring.HitFactor:
      return 'HF';
  }
};

const deriveVariant = (dto: QuickStartDto): QuickStartVariant => {
  if (!dto.hasAnyDrill) return 'empty';
  if (dto.todayPlan !== null) return 'plan';
  return 'drills';
};

const formatRelativeDay = (date: Date, translate: TranslationService): string => {
  const diff: number = differenceInCalendarDays(new Date(), date);
  if (diff <= 0) return translate.translate('fabQuickStart.relative.today');
  if (diff === 1) return translate.translate('fabQuickStart.relative.yesterday');
  return translate.translate('fabQuickStart.relative.daysAgo', { days: diff });
};

const formatDrillsSub = (dto: QuickStartDto, translate: TranslationService): string => {
  return translate.translate('fabQuickStart.subLabel.drills', {
    drills: dto.drillCount,
    favorites: dto.favoriteCount
  });
};

const toTodayPlanVM = (
  dto: QuickStartDto['todayPlan'],
  translate: TranslationService
): QuickStartTodayPlanViewModel | null => {
  if (dto === null) return null;
  return {
    id: dto.id,
    title: dto.title ?? translate.translate('fabQuickStart.defaultPlanTitle'),
    subLabel: translate.translate('fabQuickStart.subLabel.todayPlan', {
      drills: dto.drillsCount,
      runs: dto.estimatedRuns
    })
  };
};

const toLastDrillVM = (
  dto: QuickStartDto['lastSessionDrill'],
  translate: TranslationService
): QuickStartLastDrillViewModel | null => {
  if (dto === null) return null;
  const when: string = formatRelativeDay(new Date(dto.sessionEndedAt), translate);
  const subLabel: string = `${dto.drillName} · ${scoringShort(dto.scoring)} · ${translate.translate(
    'fabQuickStart.subLabel.runsAndWhen',
    { runs: dto.runsCount, when }
  )}`;
  return { drillId: dto.drillId, subLabel };
};

export const toQuickStartViewModel = (dto: QuickStartDto, translate: TranslationService): QuickStartViewModel => ({
  variant: deriveVariant(dto),
  todayPlan: toTodayPlanVM(dto.todayPlan, translate),
  lastDrill: toLastDrillVM(dto.lastSessionDrill, translate),
  drillsSubLabel: formatDrillsSub(dto, translate),
  scoring: dto.lastSessionDrill?.scoring ?? null
});
